import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { ChevronLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useEffect } from "react";

const SommerCampsInfoPage = () => {
  const navigate = useNavigate();
  
  const handleNavigateBack = () => {
    navigate('/kompass-camps');
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleBack = () => {
    navigate('/');
    setTimeout(() => {
      const element = document.getElementById('career-journey');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  const { data: images } = useQuery({
    queryKey: ["images", "coaching-camps-info"],
    queryFn: async () => {
      console.log("Fetching images for coaching-camps-info section");
      const { data, error } = await supabase
        .from("images")
        .select("*")
        .eq("section", "coaching-camps-info")
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      // Remove duplicates based on title and keep only the newest entry for each title
      const seenTitles = new Set();
      const uniqueImages = data?.filter((image: any) => {
        if (seenTitles.has(image.title)) {
          return false;
        }
        seenTitles.add(image.title);
        return true;
      }).slice(0, 6) || [];
      
      console.log("Retrieved images after deduplication:", uniqueImages);
      return uniqueImages;
    },
  });

  const getImageUrl = (index: number) => {
    if (images && images[index]) {
      const { data } = supabase.storage
        .from('images')
        .getPublicUrl(images[index].storage_path);
      return data.publicUrl;
    }
    return 'https://images.unsplash.com/photo-1543363136-3fdb62e11be5?auto=format&fit=crop&q=80';
  };

  return (
    <div className="min-h-screen bg-gradient-to-r from-[#243949] to-[#517fa4]">
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <Button 
            variant="ghost" 
            className="text-white hover:bg-white/10 p-2 h-auto group"
            onClick={handleNavigateBack}
          >
            <div className="flex items-center animate-horizontal-bounce">
              <ChevronLeft className="w-4 h-4" />
              <ChevronLeft className="w-4 h-4 -ml-2" />
            </div>
          </Button>
          
          <h2 className="text-lg md:text-xl font-bold text-white text-center uppercase flex-grow">
            Camp Infos
          </h2>
          
          <div className="w-[40px]"></div>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            <div className="aspect-[4/3]">
              <img
                src={getImageUrl(0)}
                alt="Pfarrer Bild 1"
                className="rounded-lg object-cover w-full h-full border border-[#9F9EA1] object-[center_30%]"
              />
            </div>
            <div className="aspect-[4/3]">
              <img
                src={getImageUrl(1)}
                alt="Pfarrer Bild 2"
                className="rounded-lg object-cover w-full h-full border border-[#9F9EA1] object-[center_35%]"
              />
            </div>
          </div>

          <div className="bg-white/10 backdrop-blur-sm p-6 rounded-lg text-white space-y-4 mb-8">
            <h3 className="text-lg md:text-xl font-bold text-white text-center mb-4">
              Was erwartet dich?
            </h3>
            <ul className="text-base md:text-lg text-white/90 space-y-2 text-left">
              <li>• Du bekommst einen klaren Kompass für den Weg nach dem Abi</li>
              <li>• Ein Raum, in dem du durch kreative und spannende Einzel- & Gruppenübungen - dein Herz hörst und dir deine Stärken (und Schwächen) bewusst werden (in Form eines PAS-Diagramms - Personal Attribute Spider)</li>
              <li>• Eine 1,5-stündige one-to-one Reflexions-Session 1-2 Wochen nach dem Camp</li>
              <li>• Ein klarer 5-Stufen-Fahrplan, wie ihr nach dem Camp zu eurer ganz konkreten Berufs-/Studienentscheidung kommt</li>
              <li>• Eine mega Zeit in einer unfassbar schönen Landschaft mit einer super Gemeinschaft in einer Abgelegenheit und Wildnis, wie du es noch nie erlebt hast.</li>
              <li>• Eine Erfahrung, wie du es schaffst, deine (scheinbaren) Grenzen zu überwinden - durch einen SkyWalk am 220m hohen Verzasca-Staudamm (Drehort von James Bond Goldeneye)</li>
            </ul>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            <div className="aspect-[4/3]">
              <img
                src={getImageUrl(2)}
                alt="Pfarrer Bild 3"
                className="rounded-lg object-cover w-full h-full border border-[#9F9EA1]"
              />
            </div>
            <div className="aspect-[4/3]">
              <img
                src={getImageUrl(3)}
                alt="Pfarrer Bild 4"
                className="rounded-lg object-cover w-full h-full border border-[#9F9EA1] object-[center_10%]"
              />
            </div>
          </div>

          <div className="bg-white/10 backdrop-blur-sm p-6 rounded-lg text-white space-y-4 mb-8">
            <h3 className="text-lg md:text-xl font-bold text-white text-center mb-4">
              Hard Facts
            </h3>
            <ul className="text-base md:text-lg text-white/90 space-y-2 text-left">
              <li>• Termin: 2.-5. Okt (Do nach der Schule 14 Uhr bis So Abend)</li>
              <li>• Ort: Vale Grande, Italien, Nähe Premosello (Nördlich des Lago Maggiores)</li>
              <li>• Leitung: Torsten Resack + weibliche Begleitperson (Sozialpädagogin)</li>
              <li>• Leistungen: Fahrt & Vollverpflegung für 4 Tage (Selbstversorgung), 4 Tage Intensiv-Coaching Berufsorientierung, James-Bond-Skywalk am Verzasca-Staudamm</li>
              <li>• Kosten: 550 EUR (Starter-Preis)</li>
            </ul>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            <div className="aspect-[4/3]">
              <img
                src={getImageUrl(4)}
                alt="Pfarrer Bild 5"
                className="rounded-lg object-cover w-full h-full border border-[#9F9EA1]"
              />
            </div>
            <div className="aspect-[4/3]">
              <img
                src={getImageUrl(5)}
                alt="Pfarrer Bild 6"
                className="rounded-lg object-cover w-full h-full border border-[#9F9EA1]"
              />
            </div>
          </div>

          <div className="bg-white/10 backdrop-blur-sm p-6 rounded-lg text-white space-y-4 mb-8">
            <h3 className="text-lg md:text-xl font-bold text-white text-center mb-4">
              Infos Torsten Resack
            </h3>
            <ul className="text-base md:text-lg text-white/90 space-y-2 text-left">
              <li>• Organisator: Torsten Resack</li>
              <li>• Link LinkedIn-Profil Torsten Resack: <a href="https://de.linkedin.com/in/torsten-t-resack-2b234b151" target="_blank" rel="noopener noreferrer" className="text-blue-300 hover:text-blue-200 underline">LinkedIn-Profil</a></li>
              <li>• Ehemaliger Ev. Pfarrer & Co-Studienleiter Studienprogramm "Studium in Israel, Jerusalem"</li>
              <li>• Unternehmensberater seit 2017</li>
            </ul>
          </div>

          <div className="bg-white/10 backdrop-blur-sm p-6 rounded-lg text-white space-y-4 mb-8">
            <h3 className="text-lg md:text-xl font-bold text-white text-center mb-4">
              Weitere Camps und Angebote
            </h3>
            <ul className="text-base md:text-lg text-white/90 space-y-2 text-left">
              <li>• Herbst-Camp (Herbstferien): 1.- 5. Nov (Sa-Morgen bis Mi-Nachmittag) (595 EUR)</li>
              <li>• Silvester-Camp: 28.Dez-2.Jan (So-Morgen bis Do-Abend) (645 EUR)</li>
              <li>• Weitere Camps 2026 folgen in Kürze</li>
            </ul>
          </div>

          <div className="bg-white/10 backdrop-blur-sm p-6 rounded-lg text-white mb-8 border-2 border-[#ECA505]">
            <p className="text-base md:text-lg text-white text-center">
              <strong>Weitere Infos sowie Anmeldung unter:</strong> torstenresack@gmail.com oder +49 (0)1573-80 99 275.
            </p>
          </div>

          <div className="flex justify-center mt-8">
            <Button 
              variant="ghost" 
              className="text-white hover:bg-white/10 p-2 h-auto group"
              onClick={handleBack}
            >
              <div className="flex items-center animate-horizontal-bounce">
                <ChevronLeft className="w-4 h-4" />
                <ChevronLeft className="w-4 h-4 -ml-2" />
              </div>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SommerCampsInfoPage;